import styled from '@emotion/styled';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AutoSaveInputLabel from '../../../../../../components/form/AutoSaveInputLabel';
import { PlainDateInput } from '../../../../../../components/form/Datepicker';
import { FormGroup } from '../../../../../../components/form/FormGroup';
import { InputWithSave } from '../../../../../../components/form/InputsWithSave/InputWithSave';
import { RichtextInputWithSave } from '../../../../../../components/form/InputsWithSave/RichtextInputWithSave';
import { RichtextInputStyled } from '../../../../../../components/form/RichtextInput';
import InputError from '../../../../../../components/form/InputError';
import { OptionBase } from '../../../../../../components/Select/types';
import { SelectDropdown } from '../../../../../../components/Select/SelectDropdown';
import { Theme } from '../../../../../../theme';
import {
  EmployeeOption,
  EmployeeOptionLabel,
} from '../../../../components/Selects/EmployeeOptionLabel';
import { RenderOptionParams } from '../../../../components/Selects/SelectSingleBase';
import { TeamOptionLabel } from '../../../../components/Selects/TeamOptionLabel';
import { SelectSingle } from '../../../../components/Selects/SelectSingle';
import { SelectSingleEmployee } from '../../../../components/Selects/SelectSingleEmployee';
import { SelectMultiple } from '../../../../components/Selects/SelectMultiple';
import { SelectMultipleEmployees } from '../../../../components/Selects/SelectMultipleEmployees';
import { GoalHint } from '../GoalHint';
import { getVisibilityOptionStr } from '../translations';
import { GoalType } from '../types';
import {
  GoalCreateOptions,
  GoalOption,
  GoalOptions,
} from '../useGoalSelectOptions';
import { GoalKeyResultsInput } from './GoalKeyResultsInput';
import {
  GoalStatusHint,
  GoalStatusInputWithRate,
  GoalStatusInputWithRateAndSave,
} from './GoalStatusInput';
import { SelectSingleGoal } from './SelectSingleGoal';
import { GoalSidebarMode, ObjectiveFormData } from './types';
import { useCommonFieldsLogic } from './useCommonFieldsLogic';
import { useGoalCategoryOptions } from './useGoalCategoryOptions';
import { GoalResultField } from './GoalResultField';
import { canEditGoalResult } from '../../hooks/useCanEditGoalResult';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { useUserInfo } from '../../../../context/UserInfo';
import { useGoalFormContext, useFieldProps } from './goalFormState';
import { PendingFieldIndicator } from './PendingFieldIndicator';
import { EmployeeObjectiveOverridesFragment } from '../../../../__generated__/graphql';

const FieldLabel: React.FC<{
  fieldName: string;
  overrides: EmployeeObjectiveOverridesFragment | null | undefined;
  labelProps: React.ComponentProps<typeof AutoSaveInputLabel>;
}> = ({ fieldName, overrides, labelProps }) => (
  <div className="d-flex align-items-center">
    <AutoSaveInputLabel
      {...labelProps}
      pendingChanges={
        <PendingFieldIndicator fieldName={fieldName} overrides={overrides} />
      }
    />
  </div>
);

type Props = {
  mode: GoalSidebarMode;
  owner?: EmployeeOption;
  options: GoalCreateOptions | GoalOptions;
  submitField?: (
    name: keyof ObjectiveFormData,
    value: unknown,
  ) => Promise<void>;
  goalHierarchy?: React.ReactNode;
  goalAlignedTo?: React.ReactNode;
  currentGoalId?: string;
};

const i18prefix = 'performanceReview.goals2.goalSidebar';

const EMPTY_CONTENT = '<p><br></p>';

export const GoalCommonFields: React.FC<Props> = ({
  owner,
  options,
  mode,
  submitField,
  goalHierarchy,
  goalAlignedTo,
  currentGoalId,
}) => {
  const { periods, tags, employees, teams } = options;
  const allGoals = 'allGoals' in options ? options.allGoals : undefined;
  const { t } = useTranslation();
  const form = useGoalFormContext<ObjectiveFormData>();
  const { values, errors, setFieldValue, saveStatuses } = form;

  const autoSaveOpt = submitField
    ? {
        autoSave: submitField as (
          name: string,
          value: unknown,
        ) => Promise<void>,
      }
    : undefined;

  const {
    developmentFrameworkEnabled,
    objectiveResultUpdateMode,
    isManagerView,
  } = useGoalsContext();
  const { id: currentEmployeeId } = useUserInfo();
  const responsibleEmployeeId: string | undefined = values.employeeId;
  const { categories: employeeAssignedCategories, loading: categoriesLoading } =
    useGoalCategoryOptions();
  const {
    readonly,
    availableStatuses,
    availableVisibilityOptions,
    fieldVisibility,
    goalType,
    rateEnabled,
    isManualProgressEnabled,
    autoGoalInfo,
    timePeriodId,
    isWeightReadonly,
  } = useCommonFieldsLogic(mode, owner?.value ?? '');

  const categoryId: string | null = values.categoryId;

  const achievementScaleOptions = useMemo(() => {
    if (!categoryId) {
      return [];
    }
    const assignment = employeeAssignedCategories.find(
      (a) => a.category.Id === categoryId,
    );
    if (!assignment) {
      return [];
    }
    return assignment.category.achievementScales.map((scale) => ({
      value: scale.Id,
      label: scale.Name,
    }));
  }, [categoryId, employeeAssignedCategories]);

  const prevCategoryIdRef = useRef(categoryId);
  useEffect(() => {
    if (prevCategoryIdRef.current !== categoryId) {
      setFieldValue('achievementScaleId', null);
      prevCategoryIdRef.current = categoryId;
    }
  }, [categoryId, setFieldValue]);

  const timePeriodStr: string | undefined = timePeriodId
    ? options?.periods.find((x) => x.value === timePeriodId)?.periodStr
    : undefined;

  const allowedParentGoals =
    allGoals?.filter(
      (x) =>
        canBeUsedAsParentGoal(goalType, x) &&
        (!currentGoalId || x.value !== currentGoalId),
    ) ?? [];

  const isSaving = (name: keyof ObjectiveFormData) =>
    saveStatuses[name]?.loading ?? false;

  const getSaveInfo = (name: keyof ObjectiveFormData) =>
    saveStatuses[name] ?? {};

  const overrides = values.overrides;

  const renderField = (
    name: keyof ObjectiveFormData,
    renderInput: (name: string, isReadonly: boolean) => React.ReactNode,
    fieldOptions: {
      optional?: boolean;
      hintText?: string;
      wrap?: boolean;
      pendingFieldName?: string;
    } = { wrap: true },
  ) => {
    const label = t(`${i18prefix}.formFields.${name}`);
    const fieldName = fieldOptions?.pendingFieldName ?? name;
    return (
      <FormGroup data-field={name}>
        <FieldLabel
          fieldName={fieldName}
          overrides={overrides}
          labelProps={{
            label,
            optional: fieldOptions?.optional,
            hint: fieldOptions?.hintText ? (
              <GoalHint text={fieldOptions.hintText} />
            ) : undefined,
            ...getSaveInfo(name),
          }}
        />
        {fieldOptions?.wrap ? (
          <StandardHeight>
            {renderInput(name, readonly || isSaving(name))}
          </StandardHeight>
        ) : (
          renderInput(name, readonly || isSaving(name))
        )}
      </FormGroup>
    );
  };

  const renderTeamOption = useCallback(
    ({ option, isInsideSelect }: RenderOptionParams<OptionBase>) => {
      return (
        <TeamOptionLabel option={option} isInsideSelect={isInsideSelect} />
      );
    },
    [],
  );

  // Field props for fields that need auto-save
  const employeeIdField = useFieldProps('employeeId', autoSaveOpt);
  const teamIdField = useFieldProps('teamId', autoSaveOpt);
  const nameField = useFieldProps('name', autoSaveOpt);
  const descriptionField = useFieldProps('description', autoSaveOpt);
  const categoryIdField = useFieldProps('categoryId', autoSaveOpt);
  const achievementScaleIdField = useFieldProps(
    'achievementScaleId',
    autoSaveOpt,
  );
  const tagIdsField = useFieldProps('tagIds', autoSaveOpt);
  const weightField = useFieldProps('weight', autoSaveOpt);
  const keyResultsField = useFieldProps('keyResults', autoSaveOpt);
  const parentObjectiveIdField = useFieldProps(
    'parentObjectiveId',
    autoSaveOpt,
  );
  const visibilityField = useFieldProps('visibility', autoSaveOpt);
  const sharedWithEmployeeIdsField = useFieldProps(
    'sharedWithEmployeeIds',
    autoSaveOpt,
  );
  const timePeriodIdField = useFieldProps('timePeriodId', autoSaveOpt);
  const startDateField = useFieldProps('startDate', autoSaveOpt);
  const dueDateField = useFieldProps('dueDate', autoSaveOpt);
  const statusWithRateField = useFieldProps('statusWithRate', autoSaveOpt);
  const manualProgressField = useFieldProps('manualProgress', autoSaveOpt);
  const objectiveResultField = useFieldProps('objectiveResult', autoSaveOpt);

  return (
    <>
      <Row>
        {(fieldVisibility.employeeId || fieldVisibility.teamId) && (
          <Col>
            {fieldVisibility.employeeId &&
              renderField('employeeId', (_name, isReadonly) => (
                <SelectSingleEmployee
                  value={employeeIdField.value ?? null}
                  onChange={(val) => {
                    employeeIdField.onChange(val ?? undefined);
                  }}
                  isDisabled={isReadonly}
                  options={employees}
                />
              ))}
            {fieldVisibility.teamId &&
              renderField('teamId', (_name, isReadonly) => (
                <SelectSingle
                  value={teamIdField.value ?? null}
                  onChange={(val) => {
                    teamIdField.onChange(val ?? undefined);
                  }}
                  isDisabled={isReadonly}
                  renderOption={renderTeamOption}
                  options={teams}
                />
              ))}
          </Col>
        )}
        <Col>
          {owner && (
            <>
              <AutoSaveInputLabel label={t(`${i18prefix}.formFields.owner`)} />
              <StandardHeight>
                <EmployeeOptionLabel employee={owner} isNameVisible={true} />
              </StandardHeight>
            </>
          )}
        </Col>
      </Row>
      {mode === 'update' ? (
        <>
          <FormGroup data-field="name">
            <InputWithSave
              value={nameField.value ?? ''}
              onChange={(val) => nameField.onChange(String(val ?? ''))}
              labelComponent={
                <FieldLabel
                  fieldName="name"
                  overrides={overrides}
                  labelProps={{
                    label: t(`${i18prefix}.formFields.name`),
                    ...getSaveInfo('name'),
                  }}
                />
              }
              placeholder={t(`${i18prefix}.placeholders.name`)}
              disabled={readonly}
            />
            {nameField.error && <InputError error={nameField.error} />}
          </FormGroup>
          <div className="mb-3">
            <RichtextInputWithSave
              value={descriptionField.value ?? ''}
              onChange={(val) => descriptionField.onChange(val ?? '')}
              labelComponent={
                <FieldLabel
                  fieldName="description"
                  overrides={overrides}
                  labelProps={{
                    label: t(`${i18prefix}.formFields.description`),
                    optional: true,
                    ...getSaveInfo('description'),
                  }}
                />
              }
              readOnly={readonly}
            />
          </div>
        </>
      ) : (
        <>
          <FormGroup data-field="name">
            <AutoSaveInputLabel label={t(`${i18prefix}.formFields.name`)} />
            <Form.Control
              value={nameField.value ?? ''}
              onChange={(e) => nameField.onChange(e.target.value)}
              onBlur={nameField.onBlur}
              placeholder={t(`${i18prefix}.placeholders.name`)}
              disabled={readonly}
              isInvalid={!!nameField.error}
            />
            {nameField.error && <InputError error={nameField.error} />}
          </FormGroup>
          <div className="mb-3">
            <AutoSaveInputLabel
              label={t(`${i18prefix}.formFields.description`)}
              optional
            />
            <RichtextInputStyled
              value={descriptionField.value ?? ''}
              onChange={(val) => {
                const result = val === EMPTY_CONTENT ? '' : val;
                descriptionField.onChange(result);
              }}
              readOnly={readonly}
            />
          </div>
        </>
      )}
      {fieldVisibility.categoryId &&
        renderField(
          'categoryId',
          (_name, isReadonly) => (
            <SelectSingle
              value={categoryIdField.value ?? null}
              onChange={(val) => categoryIdField.onChange(val)}
              isDisabled={employeeAssignedCategories.length === 0 || isReadonly}
              isLoading={categoriesLoading}
              options={employeeAssignedCategories.map((assignment) => ({
                value: assignment.category.Id,
                label: assignment.category.Name,
              }))}
            />
          ),
          { optional: true },
        )}
      {fieldVisibility.achievementScaleId &&
        achievementScaleOptions.length > 0 &&
        renderField(
          'achievementScaleId',
          (_name, isReadonly) => (
            <SelectSingle
              value={achievementScaleIdField.value ?? null}
              onChange={(val) => achievementScaleIdField.onChange(val)}
              isDisabled={isReadonly}
              options={achievementScaleOptions}
            />
          ),
          { optional: true },
        )}
      {fieldVisibility.objectiveResult && currentGoalId && (
        <GoalResultField
          predefinedResultOptions={
            categoryId
              ? employeeAssignedCategories.find(
                  (a) => a.category.Id === categoryId,
                )?.category.predefinedResultOptions ?? []
              : []
          }
          canEdit={canEditGoalResult({
            mode: objectiveResultUpdateMode,
            isManager: isManagerView,
            isEmployee: currentEmployeeId === responsibleEmployeeId,
          })}
          value={objectiveResultField.value}
          onChange={objectiveResultField.onChange}
          saveInfo={getSaveInfo('objectiveResult')}
          showResultValue={isManagerView}
          overrides={overrides}
        />
      )}
      {renderField(
        'tagIds',
        (_name, isReadonly) => (
          <SelectMultiple
            values={tagIdsField.value ?? []}
            onChange={(val) => tagIdsField.onChange(val)}
            isDisabled={
              options.tags.length === 0 || isReadonly || goalType === 'company'
            }
            options={tags}
          />
        ),
        { optional: true, wrap: false },
      )}

      {fieldVisibility.weight &&
        (mode === 'update' ? (
          <FormGroup data-field="weight">
            <InputWithSave
              type="number"
              value={weightField.value ?? undefined}
              onChange={(val) =>
                weightField.onChange(
                  val !== undefined && val !== '' ? Number(val) : null,
                )
              }
              labelComponent={
                <FieldLabel
                  fieldName="weight"
                  overrides={overrides}
                  labelProps={{
                    label: t(`${i18prefix}.formFields.weight`),
                    optional: true,
                    ...getSaveInfo('weight'),
                  }}
                />
              }
              placeholder="0-100"
              disabled={form.isSubmitting || isWeightReadonly}
            />
          </FormGroup>
        ) : (
          renderField(
            'weight',
            (_name, isReadonly) => (
              <Form.Control
                type="number"
                value={weightField.value ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  weightField.onChange(val !== '' ? Number(val) : null);
                }}
                onBlur={weightField.onBlur}
                disabled={isReadonly}
                placeholder="0-100"
              />
            ),
            { optional: true },
          )
        ))}

      {fieldVisibility.keyResults &&
        renderField(
          'keyResults',
          (_name, isReadonly) => (
            <GoalKeyResultsInput
              values={keyResultsField.value ?? []}
              onChange={(val) => keyResultsField.onChange(val)}
              isDisabled={isReadonly}
              confirmDeletion={mode === 'update'}
            />
          ),
          { optional: true, wrap: false },
        )}

      {goalHierarchy}
      {fieldVisibility.parentObjectiveId && (
        <Row>
          <Col>
            {renderField(
              'parentObjectiveId',
              (_name, isReadonly) => (
                <SelectSingleGoal
                  value={parentObjectiveIdField.value ?? null}
                  onChange={(val) => parentObjectiveIdField.onChange(val)}
                  isDisabled={isReadonly}
                  options={allowedParentGoals}
                  placeholder=""
                />
              ),
              {
                optional: true,
                wrap: true,
                pendingFieldName: 'parentObjectiveId',
              },
            )}
          </Col>
        </Row>
      )}
      {goalAlignedTo}
      <Row>
        <Col>
          {renderField('visibility', (_name, isReadonly) => (
            <SelectDropdown
              value={visibilityField.value ?? null}
              onChange={(val) => {
                visibilityField.onChange(val);
                visibilityField.onBlur();
              }}
              isDisabled={isReadonly}
              options={availableVisibilityOptions.map((value) => ({
                value,
                label: getVisibilityOptionStr(value),
              }))}
              heightMode="large"
            />
          ))}
        </Col>
        <Col>
          {fieldVisibility.sharedWithEmployeeIds &&
            renderField('sharedWithEmployeeIds', (_name, isReadonly) => (
              <SelectMultipleEmployees
                values={sharedWithEmployeeIdsField.value ?? []}
                onChange={(val) => sharedWithEmployeeIdsField.onChange(val)}
                isDisabled={isReadonly}
                options={employees}
              />
            ))}
        </Col>
      </Row>
      {developmentFrameworkEnabled ? (
        <input type="hidden" value={values.timePeriodId ?? ''} readOnly />
      ) : (
        <Row>
          <Col>
            {renderField('timePeriodId', (_name, isReadonly) => (
              <>
                <SelectDropdown
                  value={timePeriodIdField.value ?? null}
                  onChange={(val) => {
                    timePeriodIdField.onChange(val);
                    timePeriodIdField.onBlur();
                  }}
                  isDisabled={isReadonly}
                  options={periods}
                  heightMode="large"
                />
                {timePeriodIdField.error && (
                  <InputError error={timePeriodIdField.error} />
                )}
                {timePeriodStr && <Hint className="mt-1">{timePeriodStr}</Hint>}
              </>
            ))}
          </Col>
          <Col></Col>
        </Row>
      )}
      <Row>
        <Col>
          {renderField(
            'startDate',
            (_name, isReadonly) => (
              <PlainDateInput
                value={startDateField.value ?? null}
                onChange={(val) => startDateField.onChange(val)}
                onBlur={startDateField.onBlur}
                error={!!startDateField.error}
                disabled={isReadonly}
              />
            ),
            { optional: true },
          )}
        </Col>
        <Col>
          {renderField(
            'dueDate',
            (_name, isReadonly) => (
              <PlainDateInput
                value={dueDateField.value ?? null}
                onChange={(val) => dueDateField.onChange(val)}
                onBlur={dueDateField.onBlur}
                error={!!dueDateField.error}
                disabled={isReadonly}
              />
            ),
            { optional: true },
          )}
        </Col>
      </Row>

      {fieldVisibility.statusWithRate && (
        <div data-field="statusWithRate">
          {mode === 'create' && (
            <GoalStatusInputWithRate
              value={statusWithRateField.value}
              onChange={(val) => statusWithRateField.onChange(val)}
              className="mb-4"
              isRateVisible={true}
              saveInfo={getSaveInfo('statusWithRate')}
              availableStatuses={availableStatuses}
              autoGoalInfo={autoGoalInfo}
              error={errors.statusWithRate}
            />
          )}
          {(mode === 'update' || mode === 'view') && (
            <GoalStatusInputWithRateAndSave
              value={statusWithRateField.value}
              onChange={statusWithRateField.onChange}
              className="mb-4"
              saveInfo={getSaveInfo('statusWithRate')}
              isDisabled={readonly}
              isRateVisible={rateEnabled}
              availableStatuses={availableStatuses}
              autoGoalInfo={autoGoalInfo}
              error={errors.statusWithRate}
              pendingChanges={
                <PendingFieldIndicator
                  fieldName="statusWithRate"
                  overrides={overrides}
                />
              }
            />
          )}
        </div>
      )}
      {fieldVisibility.manualProgress && (
        <div className="d-flex ms-2">
          <Form.Check
            type="checkbox"
            checked={!!manualProgressField.value}
            onChange={(e) => {
              manualProgressField.onChange(e.target.checked);
              manualProgressField.onBlur();
            }}
            disabled={readonly || !isManualProgressEnabled}
          />
          <div className="ps-2">
            <AutoSaveInputLabel
              label={t(`${i18prefix}.formFields.manualProgress`)}
              hint={<GoalStatusHint />}
              {...getSaveInfo('manualProgress')}
            />
          </div>
        </div>
      )}
    </>
  );
};

const canBeUsedAsParentGoal = (
  targetGoalType: GoalType,
  goal: GoalOption,
): boolean => {
  if (targetGoalType === 'company') {
    return false;
  }
  if (targetGoalType === 'team' && goal.goalType === 'employee') {
    return false;
  }
  return true;
};

const StandardHeight = styled('div')({
  minHeight: 'calc(1.5em + 1rem + 2px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const Hint = styled('div')({
  color: Theme.color.dark,
  whiteSpace: 'nowrap',
});
